<template>
    <div>
        <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
            <span class="font-bold">{{ props.qualification.usecase }}</span>
            <span class="text-sm text-slate-500" data-test="fees">
                monthly {{ feeLabel(props.qualification.monthly_fee) }} · quarterly
                {{ feeLabel(props.qualification.quarterly_fee) }} · annual
                {{ feeLabel(props.qualification.annual_fee) }}
            </span>
        </div>
        <vx-table v-if="props.detailed" class="xl:grid-cols-5" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>Carrier</div>
                <div>Qualify</div>
                <div>Review</div>
                <div>Class / TPM</div>
                <div>Tier / Daily cap</div>
            </vx-table-row>
            <vx-table-row
                v-for="(terms, mnoId) in props.qualification.mno_metadata"
                :key="mnoId"
                :data-test="`carrier-${mnoId}`"
            >
                <vx-table-row-title>{{ mnoLabel(mnoId, terms.mno) }}</vx-table-row-title>
                <vx-table-row-item>
                    <template v-slot:label> Qualify: </template>
                    {{ yesNo(terms.qualify) }}
                </vx-table-row-item>
                <vx-table-row-item>
                    <template v-slot:label> Review: </template>
                    {{ yesNo(terms.mno_review) }}
                </vx-table-row-item>
                <vx-table-row-item>
                    <template v-slot:label> Class / TPM: </template>
                    {{ carrierClassLabel(terms) }}
                </vx-table-row-item>
                <vx-table-row-item>
                    <template v-slot:label> Tier / Daily cap: </template>
                    {{ carrierTierLabel(terms) }}
                </vx-table-row-item>
            </vx-table-row>
        </vx-table>
        <template v-else>
            <dl class="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                <div>
                    <dt class="font-bold">AT&T class / TPM</dt>
                    <dd class="text-slate-500" data-test="att-terms">{{ att ? carrierClassLabel(att) : '—' }}</dd>
                </div>
                <div>
                    <dt class="font-bold">T-Mobile tier / daily cap</dt>
                    <dd class="text-slate-500" data-test="tmobile-terms">
                        {{ tmobile ? carrierTierLabel(tmobile) : '—' }}
                    </dd>
                </div>
            </dl>
            <div v-if="exceptions.length" class="mt-2 text-sm text-rose-600" data-test="exceptions">
                {{ exceptions.join(' · ') }}
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { VxTable, VxTableRow, VxTableRowItem, VxTableRowTitle } from '@voxie/frontend-components';
import { computed } from 'vue';
import {
    carrierClassLabel,
    carrierTierLabel,
    feeLabel,
    MNO_ATT,
    MNO_TMOBILE,
    mnoLabel,
    yesNo,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import type { TcrMnoTerms, TcrUsecaseQualification } from '~/services/adminTenDlcRegistrationClient.types';

const props = withDefaults(defineProps<{ qualification: TcrUsecaseQualification; detailed?: boolean }>(), {
    detailed: false,
});

const att = computed(() => props.qualification.mno_metadata[MNO_ATT]);
const tmobile = computed(() => props.qualification.mno_metadata[MNO_TMOBILE]);

const carriersWhere = (predicate: (terms: TcrMnoTerms) => boolean): string[] =>
    Object.entries(props.qualification.mno_metadata)
        .filter(([, terms]) => predicate(terms))
        .map(([mnoId, terms]) => mnoLabel(mnoId, terms.mno));

const exceptions = computed(() => {
    const notQualified = carriersWhere((terms) => false === terms.qualify);
    const needsReview = carriersWhere((terms) => true === terms.mno_review);

    return [
        ...(notQualified.length ? [`Not qualified: ${notQualified.join(', ')}`] : []),
        ...(needsReview.length ? [`Needs review: ${needsReview.join(', ')}`] : []),
    ];
});
</script>
